import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { selectShopifyParams } from "../store/settings/settingsSlice";
import {
  appendShopifyParams,
  mergeShopifyParams,
  pickShopifyParams,
} from "../utils/shopifyParams";
import { useShopifyApp } from "./useShopifyApp";

export const useShopifyParams = () => {
  const location = useLocation();
  const storedParams = useSelector(selectShopifyParams);

  return useMemo(() => {
    const urlParams = pickShopifyParams(
      Object.fromEntries(new URLSearchParams(location.search)),
    );

    return mergeShopifyParams(storedParams, urlParams);
  }, [location.search, storedParams]);
};

export const useShopifyNavigate = () => {
  const navigate = useNavigate();
  const isShopifyApp = useShopifyApp();
  const shopifyParams = useShopifyParams();

  const shopifyNavigate = useCallback(
    (to, options) => {
      if (typeof to === "string" && isShopifyApp) {
        navigate(appendShopifyParams(to, shopifyParams), options);
        return;
      }

      navigate(to, options);
    },
    [navigate, isShopifyApp, shopifyParams],
  );

  const shopifyOpen = useCallback(
    (url, target, features) => {
      const nextTarget = target ?? "_blank";
      const nextUrl =
        isShopifyApp && typeof url === "string"
          ? appendShopifyParams(url, shopifyParams)
          : url;

      window.open(nextUrl, nextTarget, features);
    },
    [isShopifyApp, shopifyParams],
  );

  const shopifyAssign = useCallback(
    (url) => {
      const nextUrl =
        isShopifyApp && typeof url === "string"
          ? appendShopifyParams(url, shopifyParams)
          : url;

      window.location.assign(nextUrl);
    },
    [isShopifyApp, shopifyParams],
  );

  return {
    navigate: shopifyNavigate,
    open: shopifyOpen,
    assign: shopifyAssign,
    shopifyParams,
  };
};
